/**
 * impact_decals.ts — where the shots LAND (2026-09-08: "if I shoot the ground
 * add a bullet hole decal that does not stay there forever, and add decals for
 * weapon damage including from ships … on structures, other ships and the
 * ground").
 *
 * Pooled camera-independent QUADS laid on the hit surface: a bullet HOLE
 * (ballistic guns), a SCORCH (energy guns) or a BLAST (ship cannon), each a
 * canvas-baked texture, oriented to the surface normal with a random spin,
 * lifted DECAL_LIFT_M off the surface with a polygon offset against
 * z-fighting, fading over the last DECAL_FADE_S of a bounded life and then
 * returned to the pool — nothing stays forever, nothing is ever disposed
 * (the GPU shield law), and DECAL_MAX caps the world. A decal can ride a
 * moving PARENT (a ship hull: the quad is re-expressed in the parent's frame
 * and added to it) so damage stays where it struck.
 *
 * The engine's DecalPool projects DecalGeometry onto one mesh; the surfaces
 * here are InstancedMesh batches, trimesh terrain and moving hulls, so flat
 * quads on the physics/analytic surface are the robust choice.
 */
import * as THREE from 'three/webgpu';
import { markMrtOverlay } from 'enginex/render/post/mrtOverlay.js';
import { activePreset } from './quality_tiers.js';
import { decalNormal, stage3Stats } from './stage3_fx.js';
import { solidToLocal, solidToWorld, solidDirToWorld } from './structure_seat.js';

export type DecalKind = 'hole' | 'scorch' | 'blast' | 'char';

export const DECAL_MAX = 200;
/** Seconds each kind stays before its fade. */
export const DECAL_LIFE_S: Record<DecalKind, number> = { hole: 30, scorch: 45, blast: 75, char: 600 };
/** Fade-out length (s) at the end of a life. */
export const DECAL_FADE_S = 6;
/** Quad size (m) per kind. */
export const DECAL_SIZE_M: Record<DecalKind, number> = { hole: 0.16, scorch: 0.34, blast: 1.7, char: 3.0 };
/** Lift off the surface (m) — with the polygon offset, never z-fights. */
export const DECAL_LIFT_M = 0.03;

interface Live {
  mesh: THREE.Mesh;
  mat: THREE.Material & { opacity: number };
  /** Built lit (MeshStandard + baked normals) or flat (MeshBasic). */
  lit: boolean;
  kind: DecalKind;
  age: number;
  life: number;
  parent: THREE.Object3D;
}

/** Stage 3: the tangent-space normal map of a crater/scorch (lib/stage3_fx `decalNormal` is the oracle). */
function bakeNormal(kind: DecalKind): THREE.Texture {
  const n = kind === 'blast' ? 128 : 64;
  const data = new Uint8Array(n * n * 4);
  for (let y = 0; y < n; y++) for (let x = 0; x < n; x++) {
    // A fire's CHAR mark is a scorch's shallow bowl at three metres (the stage-3 oracle knows three profiles).
    const [nx, ny, nz] = decalNormal(kind === 'char' ? 'scorch' : kind, (x + 0.5) / n, (y + 0.5) / n);
    const i = (y * n + x) * 4;
    data[i] = Math.round((nx * 0.5 + 0.5) * 255); data[i + 1] = Math.round((ny * 0.5 + 0.5) * 255); data[i + 2] = Math.round((nz * 0.5 + 0.5) * 255); data[i + 3] = 255;
  }
  const t = new THREE.DataTexture(data, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.colorSpace = THREE.NoColorSpace; t.minFilter = THREE.LinearFilter; t.magFilter = THREE.LinearFilter; t.needsUpdate = true;
  return t;
}

function bake(kind: DecalKind): THREE.Texture {
  const n = kind === 'blast' || kind === 'char' ? 128 : kind === 'scorch' ? 96 : 64;
  const c = document.createElement('canvas');
  c.width = c.height = n;
  const g = c.getContext('2d')!;
  const cx = n / 2, cy = n / 2;
  // deterministic grit: a tiny LCG so every bake is the same picture
  let seed = kind === 'hole' ? 7 : kind === 'scorch' ? 11 : kind === 'char' ? 17 : 13;
  const rnd = (): number => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
  if (kind === 'char') {
    // THE FIRE'S CHAR (fire realism R2, 2026-09-25): black char under the laid fire, a ring of grey-white ash, a
    // brown scorched halo that fades into the ground, and soot flecks thrown by the pops.
    const grad = g.createRadialGradient(cx, cy, 0, cx, cy, n / 2);
    grad.addColorStop(0, 'rgba(14,12,10,0.95)');
    grad.addColorStop(0.22, 'rgba(22,19,16,0.9)');
    grad.addColorStop(0.34, 'rgba(92,88,82,0.55)');
    grad.addColorStop(0.42, 'rgba(48,40,32,0.5)');
    grad.addColorStop(0.7, 'rgba(58,44,30,0.22)');
    grad.addColorStop(1, 'rgba(58,44,30,0)');
    g.fillStyle = grad; g.fillRect(0, 0, n, n);
    for (let i = 0; i < 70; i++) {
      const a = rnd() * Math.PI * 2, r = n * (0.08 + rnd() * 0.36);
      const pale = rnd() < 0.35;
      g.fillStyle = pale ? `rgba(150,146,138,${0.2 + rnd() * 0.3})` : `rgba(16,13,10,${0.25 + rnd() * 0.45})`;
      g.beginPath(); g.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 0.8 + rnd() * 2.2, 0, Math.PI * 2); g.fill();
    }
  } else if (kind === 'hole') {
    const grad = g.createRadialGradient(cx, cy, 0, cx, cy, n / 2);
    grad.addColorStop(0, 'rgba(8,6,4,1)');
    grad.addColorStop(0.3, 'rgba(14,10,7,0.95)');
    grad.addColorStop(0.5, 'rgba(40,30,22,0.55)');
    grad.addColorStop(0.72, 'rgba(60,48,36,0.22)');
    grad.addColorStop(1, 'rgba(60,48,36,0)');
    g.fillStyle = grad; g.fillRect(0, 0, n, n);
    // chipped rim: a few dark flecks
    for (let i = 0; i < 9; i++) {
      const a = rnd() * Math.PI * 2, r = n * (0.3 + rnd() * 0.16);
      g.fillStyle = `rgba(20,14,10,${0.35 + rnd() * 0.4})`;
      g.beginPath(); g.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, 1.5 + rnd() * 2.5, 0, Math.PI * 2); g.fill();
    }
  } else {
    const grad = g.createRadialGradient(cx, cy, 0, cx, cy, n / 2);
    grad.addColorStop(0, kind === 'blast' ? 'rgba(10,8,6,0.95)' : 'rgba(12,10,8,0.9)');
    grad.addColorStop(0.35, 'rgba(24,18,14,0.75)');
    grad.addColorStop(0.65, 'rgba(36,28,22,0.32)');
    grad.addColorStop(1, 'rgba(36,28,22,0)');
    g.fillStyle = grad; g.fillRect(0, 0, n, n);
    // radial streaks: the char thrown outward
    const streaks = kind === 'blast' ? 26 : 14;
    for (let i = 0; i < streaks; i++) {
      const a = rnd() * Math.PI * 2, r0 = n * (0.18 + rnd() * 0.1), r1 = n * (0.4 + rnd() * 0.12);
      g.strokeStyle = `rgba(18,14,10,${0.25 + rnd() * 0.35})`;
      g.lineWidth = 1 + rnd() * (kind === 'blast' ? 3 : 2);
      g.beginPath(); g.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0); g.lineTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1); g.stroke();
    }
    if (kind === 'blast') {
      // a faint ember ring right at the crater lip
      g.strokeStyle = 'rgba(255,140,60,0.18)'; g.lineWidth = 2;
      g.beginPath(); g.arc(cx, cy, n * 0.2, 0, Math.PI * 2); g.stroke();
    }
  }
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}

export class ImpactDecals {
  readonly root = new THREE.Group();
  private readonly geo = new THREE.PlaneGeometry(1, 1);
  private readonly tex: Record<DecalKind, THREE.Texture> | null;
  private normals: Record<DecalKind, THREE.Texture> | null = null;
  private readonly free: Live[] = [];
  private readonly live: Live[] = [];
  readonly counters = { placed: 0, live: 0, expired: 0, evicted: 0 };
  private readonly _q = new THREE.Quaternion();
  private readonly _q2 = new THREE.Quaternion();
  private readonly _n = new THREE.Vector3();
  private readonly _p = new THREE.Vector3();
  private readonly _z = new THREE.Vector3(0, 0, 1);
  private rnd = 0.137;

  constructor(scene: THREE.Scene) {
    this.root.name = 'impact-decals';
    scene.add(this.root);
    this.tex = typeof document !== 'undefined' ? { hole: bake('hole'), scorch: bake('scorch'), blast: bake('blast'), char: bake('char') } : null;
  }

  private make(kind: DecalKind, lit: boolean): Live {
    let mat: THREE.Material & { opacity: number };
    if (lit) {
      // Stage 3: a LIT decal — the mark shades with the scene's light and its
      // baked crater normals give a hole a rim and a blast a lip (a flat unlit
      // sticker reads pasted-on under a low sun).
      if (!this.normals) this.normals = { hole: bakeNormal('hole'), scorch: bakeNormal('scorch'), blast: bakeNormal('blast'), char: bakeNormal('char') };
      mat = new THREE.MeshStandardMaterial({
        map: this.tex ? this.tex[kind] : null, color: 0xffffff, transparent: true, opacity: 1,
        normalMap: this.normals[kind], normalScale: new THREE.Vector2(1.4, 1.4), roughness: 0.92, metalness: 0,
        depthWrite: false, side: THREE.DoubleSide,
        polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
      });
      stage3Stats.litDecals++;
    } else {
      mat = new THREE.MeshBasicMaterial({
        map: this.tex ? this.tex[kind] : null, color: 0xffffff, transparent: true, opacity: 1,
        depthWrite: false, toneMapped: false, side: THREE.DoubleSide,
        polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
      });
    }
    markMrtOverlay(mat); // a mark ON a surface leaves that surface's normal to the post passes (AO)
    const mesh = new THREE.Mesh(this.geo, mat);
    mesh.frustumCulled = false;
    mesh.renderOrder = 8;
    mesh.name = `decal-${kind}`;
    return { mesh, mat, lit, kind, age: 0, life: DECAL_LIFE_S[kind], parent: this.root };
  }

  /** Lay a decal of `kind` at `point` on a surface facing `normal` (world
   *  space). `parent`: a moving hull the mark should ride. `sizeMul` scales
   *  the kind's quad. Returns the mesh (probes read its transform). */
  place(kind: DecalKind, point: THREE.Vector3, normal: THREE.Vector3, opts: { parent?: THREE.Object3D | null; sizeMul?: number; lifeS?: number; flat?: boolean } = {}): THREE.Mesh {
    // `flat` (slice K, 2026-10-02): a mark on an UNLIT surface — an interior's shell and furniture (interiors.md: the
    // room is unlit by law) — must be unlit too, or it answers a sun the wall it lies on never sees (black by night).
    const lit = !opts.flat && !!activePreset().litDecals;
    let l: Live | null = null;
    { // the pool is per kind AND per material flavour (lit/flat): pick a match, else make one
      const i = this.free.findIndex((f) => f.kind === kind && f.lit === lit);
      if (i >= 0) l = this.free.splice(i, 1)[0];
    }
    if (!l) {
      if (this.live.length + this.free.length >= DECAL_MAX) {
        const old = this.live.shift();
        if (old) { this.retire(old); this.counters.evicted++; const i = this.free.indexOf(old); if (i >= 0) this.free.splice(i, 1); }
      }
      l = this.make(kind, lit);
    }
    this._n.copy(normal);
    if (this._n.lengthSq() < 1e-8) this._n.set(0, 1, 0);
    this._n.normalize();
    // face the surface: +Z of the quad along the normal, then a random spin about it
    this._q.setFromUnitVectors(this._z, this._n);
    this.rnd = (this.rnd * 9301 + 49297) % 233280;
    const spin = (this.rnd / 233280) * Math.PI * 2;
    this._q2.setFromAxisAngle(this._n, spin);
    this._q.premultiply(this._q2);
    this._p.copy(point).addScaledVector(this._n, DECAL_LIFT_M);
    const size = DECAL_SIZE_M[kind] * (opts.sizeMul ?? 1) * (0.88 + (this.rnd % 1000) / 1000 * 0.3);
    const parent = opts.parent ?? this.root;
    const m = l.mesh;
    if (parent !== this.root) {
      // ride the hull: express the world pose in the parent's frame
      parent.updateMatrixWorld(true);
      m.position.copy(parent.worldToLocal(this._p.clone()));
      const pw = parent.getWorldQuaternion(new THREE.Quaternion()).invert();
      m.quaternion.copy(pw.multiply(this._q));
      const ps = parent.getWorldScale(new THREE.Vector3());
      m.scale.set(size / Math.max(1e-6, ps.x), size / Math.max(1e-6, ps.y), 1);
    } else {
      m.position.copy(this._p);
      m.quaternion.copy(this._q);
      m.scale.set(size, size, 1);
    }
    l.parent = parent;
    parent.add(m);
    l.mat.opacity = 1;
    m.visible = true;
    l.age = 0;
    l.life = opts.lifeS ?? DECAL_LIFE_S[kind];
    this.live.push(l);
    this.counters.placed++;
    this.counters.live = this.live.length;
    return m;
  }

  private retire(l: Live): void {
    l.parent.remove(l.mesh);
    l.mesh.visible = false;
    l.parent = this.root;
    if (this.free.length < DECAL_MAX) this.free.push(l);
  }

  /** Age every decal; fade the last DECAL_FADE_S; release the expired. */
  update(dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const l = this.live[i];
      l.age += dt;
      const left = l.life - l.age;
      if (left <= 0) { this.live.splice(i, 1); this.retire(l); this.counters.expired++; continue; }
      l.mat.opacity = left < DECAL_FADE_S ? Math.max(0, left / DECAL_FADE_S) : 1;
    }
    this.counters.live = this.live.length;
  }

  /** Retire one decal early (a fire that went out takes its char mark with it). Returns true if it was live. */
  release(mesh: THREE.Mesh): boolean {
    const i = this.live.findIndex((l) => l.mesh === mesh);
    if (i < 0) return false;
    const l = this.live.splice(i, 1)[0];
    this.retire(l);
    this.counters.live = this.live.length;
    return true;
  }

  /** Probe diag: every live decal's kind, world position, normal (+Z of the quad), age and parent. */
  list(): Array<{ kind: DecalKind; at: number[]; normal: number[]; age: number; life: number; opacity: number; parent: string; size: number }> {
    const p = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3();
    return this.live.map((l) => {
      l.mesh.getWorldPosition(p); l.mesh.getWorldQuaternion(q); l.mesh.getWorldScale(s);
      const n = new THREE.Vector3(0, 0, 1).applyQuaternion(q);
      return { kind: l.kind, at: [+p.x.toFixed(2), +p.y.toFixed(2), +p.z.toFixed(2)], normal: [+n.x.toFixed(2), +n.y.toFixed(2), +n.z.toFixed(2)], age: +l.age.toFixed(1), life: l.life, opacity: +l.mat.opacity.toFixed(2), parent: l.parent === this.root ? 'world' : (l.parent.name || l.parent.type), size: +s.x.toFixed(2) };
    });
  }

  /** Probe lever: age every live decal by `s` seconds at once. */
  ageAll(s: number): void { for (const l of this.live) l.age += s; }

  dispose(): void {
    for (const l of this.live) l.parent.remove(l.mesh);
    this.live.length = 0;
    this.root.parent?.remove(this.root);
    this.geo.dispose();
    for (const l of this.free) l.mat.dispose();
    this.free.length = 0;
    if (this.tex) for (const t of Object.values(this.tex)) t.dispose();
  }
}

/** THE MARK SITS ON THE MESH (2026-09-08, "the decals were floating around my
 *  ship"): cast a ray from `from` toward `toward` against an object's meshes
 *  and return the first surface point with its world-space face normal, or
 *  null when nothing is hit within `maxDist`. Bounding boxes lie about hulls
 *  (a winged ship fills a fifth of its box) — only the surface is the truth. */
const _rc = new THREE.Raycaster();
const _dir = new THREE.Vector3();
const _nm = new THREE.Matrix3();
export function surfacePointOn(obj: THREE.Object3D, from: THREE.Vector3, toward: THREE.Vector3, maxDist = Infinity): { point: THREE.Vector3; normal: THREE.Vector3 } | null {
  _dir.subVectors(toward, from);
  const len = _dir.length();
  if (len < 1e-6) return null;
  _dir.divideScalar(len);
  _rc.set(from, _dir);
  _rc.near = 0;
  _rc.far = Math.min(maxDist, Infinity);
  (_rc as unknown as { firstHitOnly?: boolean }).firstHitOnly = true; // three-mesh-bvh honours it; plain three ignores it
  obj.updateMatrixWorld(true);
  const hits = _rc.intersectObject(obj, true);
  for (const h of hits) {
    const mesh = h.object as THREE.Mesh;
    if (!mesh.isMesh || !mesh.visible) continue;
    let n: THREE.Vector3;
    if (h.face) {
      _nm.getNormalMatrix(mesh.matrixWorld);
      n = h.face.normal.clone().applyMatrix3(_nm).normalize();
      if (n.dot(_dir) > 0) n.negate(); // the face we see: turned toward the ray's origin
    } else n = _dir.clone().negate();
    return { point: h.point.clone(), normal: n };
  }
  return null;
}

/** The point on the nearest face of a structure solid's box for a hit at
 *  (wx, y, wz) inside/near it (pure): the roof plane when in the roof band,
 *  else the nearest side plane — a decal sits ON the box, never inside it. */
export function structureFacePoint(s: { x: number; z: number; hx: number; hz: number; yaw: number; base: number; top: number }, wx: number, y: number, wz: number, roofBand = 1.5): { x: number; y: number; z: number } {
  if (y >= s.top - roofBand) return { x: wx, y: s.top, z: wz };
  // THE SOLID FRAME IS THREE'S (2026-09-23): a solid's yaw is `rotation.y`, which maps
  //   local (x, z) → world (x·cos yaw + z·sin yaw, −x·sin yaw + z·cos yaw),
  //   so world → local is the transpose: lx = dx·cos yaw − dz·sin yaw, lz = dx·sin yaw + dz·cos yaw.
  // The first cut used the STANDARD 2D rotation (lx = dx·cos + dz·sin …) — the MIRROR of
  // `structure_solids.toLocal`, so on a diagonal non-square solid the face and the point
  // were computed for a box drawn at twice its angle (the mark hung off the wall).
  let { lx, lz } = solidToLocal(s, wx, wz);
  const fx = Math.abs(lx) / Math.max(1e-6, s.hx), fz = Math.abs(lz) / Math.max(1e-6, s.hz);
  if (fx >= fz) lx = lx >= 0 ? s.hx : -s.hx; else lz = lz >= 0 ? s.hz : -s.hz;
  const p = solidToWorld(s, lx, lz);
  return { x: p.x, y: Math.max(s.base, Math.min(s.top, y)), z: p.z };
}

/** The outward normal of the nearest face of a structure solid's box at a
 *  point inside/on it (pure): the roof when the point is within `roofBand`
 *  of the top, else the side whose face is closest in the box's own frame,
 *  turned back to world by the solid's yaw. */
export function structureFaceNormal(s: { x: number; z: number; hx: number; hz: number; yaw: number; base: number; top: number }, wx: number, y: number, wz: number, roofBand = 1.5): { x: number; y: number; z: number } {
  if (y >= s.top - roofBand) return { x: 0, y: 1, z: 0 };
  // Three's frame, like structureFacePoint above (the standard 2D rotation mirrored it —
  // a diagonal solid's normal pointed off a face the box does not have): world → local
  // is R(yaw)ᵀ, local → world is R(yaw) with local x → (cos, −sin), local z → (sin, cos).
  const { lx, lz } = solidToLocal(s, wx, wz);
  // which face is nearer, measured as a fraction of the half-extent
  const fx = Math.abs(lx) / Math.max(1e-6, s.hx), fz = Math.abs(lz) / Math.max(1e-6, s.hz);
  let nx: number, nz: number;
  if (fx >= fz) { nx = lx >= 0 ? 1 : -1; nz = 0; } else { nx = 0; nz = lz >= 0 ? 1 : -1; }
  const n = solidDirToWorld(s, nx, nz);
  return { x: n.x, y: 0, z: n.z };
}

/** The ground normal from a height function (pure): central differences over `h` metres. */
export function groundNormal(heightAt: (x: number, z: number) => number, x: number, z: number, h = 0.6): { x: number; y: number; z: number } {
  const dx = (heightAt(x + h, z) - heightAt(x - h, z)) / (2 * h);
  const dz = (heightAt(x, z + h) - heightAt(x, z - h)) / (2 * h);
  const nx = -dx, ny = 1, nz = -dz;
  const len = Math.hypot(nx, ny, nz) || 1;
  return { x: nx / len, y: ny / len, z: nz / len };
}

/** Walk a ray against a height function (pure): the first point at or below
 *  the ground within `maxM`, stepped every `stepM`, refined by bisection.
 *  Returns null when the ray stays above ground. */
export function rayGroundHit(
  heightAt: (x: number, z: number) => number,
  ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, maxM: number, stepM = 0.5,
): { x: number; y: number; z: number; t: number } | null {
  const len = Math.hypot(dx, dy, dz) || 1;
  dx /= len; dy /= len; dz /= len;
  let tPrev = 0;
  let above = oy - heightAt(ox, oz) > 0;
  if (!above) return { x: ox, y: oy, z: oz, t: 0 };
  for (let t = stepM; t <= maxM; t += stepM) {
    const x = ox + dx * t, y = oy + dy * t, z = oz + dz * t;
    if (y - heightAt(x, z) <= 0) {
      let lo = tPrev, hi = t;
      for (let i = 0; i < 12; i++) {
        const mid = (lo + hi) / 2;
        const my = oy + dy * mid - heightAt(ox + dx * mid, oz + dz * mid);
        if (my > 0) lo = mid; else hi = mid;
      }
      const th = hi;
      return { x: ox + dx * th, y: oy + dy * th, z: oz + dz * th, t: th };
    }
    tPrev = t;
    above = true;
  }
  return null;
}

// ── ONE field for every shooter (globalThis-backed: vite's dev-graph module
// duplication must never split the pool between WildsModule and the ships) ──
const store = globalThis as unknown as { __atlasImpactDecals?: ImpactDecals };
export function impactDecals(scene: THREE.Scene): ImpactDecals {
  if (!store.__atlasImpactDecals) store.__atlasImpactDecals = new ImpactDecals(scene);
  else if (!store.__atlasImpactDecals.root.parent) scene.add(store.__atlasImpactDecals.root);
  return store.__atlasImpactDecals;
}
export function impactDecalsIfAny(): ImpactDecals | null { return store.__atlasImpactDecals ?? null; }
